import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Breadcrumbs,
  Container,
  LinearProgress,
  IconButton,
  Grid,
  Stack,
  Typography,
  Snackbar,
  Box,
  Paper,
  Fade,
  Grow,
  alpha,
  useTheme,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import dayjs from 'dayjs';
import SpeakerImage from '../../media/Lamp Poses-06-1.jpg';
import { useApi } from '../../hooks/useApi';
import { AuthContext } from '../../AuthContext';

function Index() {
  const jApi = useApi();
  const { user, getUser } = useContext(AuthContext);
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs().startOf('day'));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Gradient header style
  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
      opacity: 0.5,
    },
  };

  const fetchJobs = async (date) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const ymd = date.format('YYYY-MM-DD');

      const response = await jApi.request('GET', `ves/job?ymd=${ymd}`);
 // ────────────────────────────────────────────────
    // Add these 4 console logs – very important
    console.log('Raw response (full object):', response);
    console.log('response type:', typeof response);
    console.log('Is array?', Array.isArray(response));
    console.log('Has .jobs property?', response && typeof response === 'object' && 'jobs' in response);
    // ────────────────────────────────────────────────

      console.log(response)
      setJobs(response.data);
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to load job history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      fetchJobs(selectedDate);
    }
  }, [selectedDate]);

  const columns = useMemo(
    () => [

      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        Cell: ({ cell }) => {
          const status = cell.getValue();
          let color = 'text.primary';
          if (status === 'completed') color = 'success.main';
          else if (status === 'failed') color = 'error.main';
          else color = 'warning.main';

          return (
            <Box component="span" sx={{ color, fontWeight: 'medium' }}>
              {status}
            </Box>
          );
        },
      },
      {
        accessorKey: 'created',
        header: 'Created (Local)',
        size: 180,
        Cell: ({ cell }) => {
          const ts = cell.getValue();
          return ts ? new Date(ts * 1000).toLocaleString() : '-';
        },
      },
      {
        accessorKey: 'completed',
        header: 'Completed (Local)',
        size: 180,
        Cell: ({ cell }) => {
          const ts = cell.getValue();
          return ts ? new Date(ts * 1000).toLocaleString() : '-';
        },
      },
      { accessorKey: 'functionpath', header: 'Function Path', size: 300 },
      {
        accessorKey: 'splat',
        header: 'Parameters (splat)',
        size: 220,
        Cell: ({ cell }) => {
          const splat = cell.getValue();
          return splat && Object.keys(splat).length > 0
            ? JSON.stringify(splat)
            : '(empty)';
        },
      }
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: jobs,
    enableSorting: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enablePagination: true,
    enableRowNumbers: true,
   
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      sorting: [{ id: 'created', desc: true }],
       density: 'compact', 
    },
    muiTableContainerProps: { sx: { maxHeight: '60vh' } },
    renderEmptyRowsFallback: () => (
      <Box p={3} textAlign="center">
        <Typography variant="h6" color="text.secondary">
          No jobs found for {selectedDate?.format('YYYY-MM-DD')}
        </Typography>
      </Box>
    ),
  });

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
    }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box
            sx={{
              pt: 3,
              pb: 2,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: alpha(theme.palette.background.paper, 0.8),
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.secondary.main, 0.1),
                    },
                  }}
                >
                  <HomeRoundedIcon
                    sx={{
                      fontSize: 20,
                      color: theme.palette.secondary.main,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.secondary.main,
                      fontWeight: 600,
                    }}
                  >
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                /
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  px: 1,
                  py: 0.5,
                }}
              >
                Processing Center
              </Typography>
            </Box>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          {/* Modern Header */}
          <Grid size={12}>
            <Grow in timeout={700}>
              <Box sx={headerGradient}>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{
                    p: 3,
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.2)}`,
                      border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                    }}
                  >
                    <img width="100%" height="100%" src={SpeakerImage} alt="Logo" style={{ objectFit: 'cover' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 0.5,
                      }}
                    >
                      Processing Center
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Currently overanalyzing your data… and judging it quietly.
                    </Typography>
                  </Box>
                </Stack>
                {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
              </Box>
            </Grow>
          </Grid>

          {/* Date Picker + Refresh */}
          <Grid size={12}>
            <Grow in timeout={800}>
              <Paper
                sx={{
                  p: 3,
                  background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.8)
                    : alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                    : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Box sx={{ flex: 1, minWidth: 250 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Select Date"
                        value={selectedDate}
                        onChange={(newValue) => {
                          if (newValue && newValue.isValid()) {
                            setSelectedDate(newValue);
                          }
                        }}
                        format="YYYY-MM-DD"
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                background: alpha(theme.palette.background.paper, 0.5),
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>

                  <IconButton
                    onClick={() => selectedDate && fetchJobs(selectedDate)}
                    disabled={isLoading || !selectedDate}
                    sx={{
                      background: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                    title="Refresh"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Stack>

                {/* Error */}
                {errorMessage && (
                  <Typography variant="body1" color="error" sx={{ mt: 2 }}>
                    {errorMessage}
                  </Typography>
                )}
              </Paper>
            </Grow>
          </Grid>

          {/* Table */}
          <Grid size={12}>
            <Grow in timeout={900}>
              <Paper
                sx={{
                  background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.8)
                    : alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                    : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                  overflow: 'hidden',
                }}
              >
                <MaterialReactTable table={table} />
              </Paper>
            </Grow>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        />
      </Container>
    </Box>
  );
}

export default Index;